import React, { useEffect, useRef } from "react";
import { Avatar, Button, Flex, Typography, List } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { ImPhoneHangUp } from "react-icons/im";
import { IoCall } from "react-icons/io5";
import { PauseCircleOutlined } from "@ant-design/icons";
import { AppDispatch, RootState } from "../../../redux/store";
import { acceptCall, rejectCall } from "../../../redux/slices/sipSlice";
import { checkIfContactSaved, extractNumberFromSip } from "../../../utils";
import "./index.scss";

const { Text } = Typography;
const HangupIcon = ImPhoneHangUp as unknown as React.FC;
const CallAcceptIcon = IoCall as unknown as React.FC;

export const useAppDispatch: () => AppDispatch = useDispatch;

const IncomingCall: React.FC = () => {
  const dispatch = useAppDispatch();
  const { calls, callerId } = useSelector((state: RootState) => state.sip);
  const { contacts } = useSelector((state: RootState) => state.contacts);
  const onGoingCallsLength = Object.keys(calls).length;

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const pendingPlayRef = useRef(false);
  const notificationRef = useRef<Notification | null>(null);

  const incomingCalls = Object.values(calls).filter(
    (call: any) => call.status === "ringing" && !call?.isGroupCall,
  );

  useEffect(() => {
    audioRef.current = new Audio("/sounds/ringtone.mp3");
    audioRef.current.loop = true;

    if (Notification.permission === "default") {
      Notification.requestPermission();
    }

    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!audioRef.current) return;

    if (incomingCalls.length > 0) {
      audioRef.current.play().catch(() => {
        pendingPlayRef.current = true;
        // If autoplay is blocked, optionally alert the user natively
        if (Notification.permission === "granted" && !notificationRef.current) {
          const callInfo = incomingCalls[0]?.info || "Unknown";
          const match = callInfo.match(/sip:([^@]+)/);
          const phoneNumber = match ? extractNumberFromSip(callInfo) || match[1] : callInfo;
          
          notificationRef.current = new Notification("Incoming Call", {
            body: `Incoming call from ${phoneNumber}`,
            requireInteraction: true,
          });

          notificationRef.current.onclick = () => {
            window.focus();
            notificationRef.current?.close();
            notificationRef.current = null;
          };
        }
      });
    } else {
      pendingPlayRef.current = false;
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      
      if (notificationRef.current) {
        notificationRef.current.close();
        notificationRef.current = null;
      }
    }
    return () => {
      if (audioRef.current && incomingCalls.length === 0) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, [incomingCalls.length]);

  useEffect(() => {
    const handleGlobalInteraction = () => {
      if (pendingPlayRef.current && audioRef.current && incomingCalls.length > 0) {
        audioRef.current.play().catch(() => {});
        pendingPlayRef.current = false;
      }
    };

    window.addEventListener("pointerdown", handleGlobalInteraction);
    window.addEventListener("keydown", handleGlobalInteraction);

    return () => {
      window.removeEventListener("pointerdown", handleGlobalInteraction);
      window.removeEventListener("keydown", handleGlobalInteraction);
    };
  }, [incomingCalls.length]);

  const getSavedContact = (callInfo: string) => {
    const match = callInfo.match(/sip:([^@]+)/);

    if (match) {
      const phoneNumber = match[1];
      const savedNumber = checkIfContactSaved(phoneNumber, contacts);
      return savedNumber;
    } else {
      return callInfo;
    }
  };

  if (incomingCalls.length === 0) return null;

  return (
    <div className="incoming-call-wrapper">
      <List
        dataSource={incomingCalls}
        renderItem={(call: any) => (
          <List.Item>
            <Flex
              justify="space-between"
              align="center"
              style={{ width: "100%" }}
            >
              <Flex align="center" gap={10}>
                <Avatar className={`avatar efone-avatar`}>
                  <Text className="user-initial">
                    {getSavedContact(call.info).slice(0, 2).toUpperCase() ||
                      "?"}
                  </Text>
                </Avatar>
                <div>
                  <div style={{ fontWeight: "bold", fontSize: 16 }}>
                    {getSavedContact(call?.info || call?.number)}
                    <span className="ringing-text"> Ringing...</span>
                  </div>
                  <div style={{ fontSize: 14, color: "#666" }}>
                    {(() => {
                      const match = call?.info?.match(/sip:([^@]+)/);
                      const phoneNumber = match ? match[1] : null;
                      const isSaved =
                        phoneNumber &&
                        checkIfContactSaved(phoneNumber, contacts);

                      return (
                        isSaved.slice(0, 1) !== "+" && (
                          <div style={{ fontSize: 14, color: "#666" }}>
                            {extractNumberFromSip(call.info) || "Unknown"}
                          </div>
                        )
                      );
                    })()}
                  </div>
                </div>
              </Flex>

              <Flex gap={onGoingCallsLength > 1 ? 20 : 12}>
                {onGoingCallsLength > 1 ? (
                  <>
                    <div
                      title="hold and accept"
                      className="cursor-pointer"
                      onClick={() => {
                        if (audioRef.current) {
                          audioRef.current.pause();
                          audioRef.current.currentTime = 0;
                        }
                        dispatch(
                          acceptCall({
                            callId: call.id,
                            sourceNumber: callerId || "",
                            actionType: "holdAndAccept",
                          }),
                        );
                      }}
                    >
                      <div className="overlappping-btns">
                        <div className="call-btn hold-btn">
                          <PauseCircleOutlined />
                        </div>
                        <div className="call-btn accept-btn">
                          <CallAcceptIcon />
                        </div>
                      </div>
                    </div>

                    <div
                      title="end and accept"
                      className="cursor-pointer"
                      onClick={() => {
                        if (audioRef.current) {
                          audioRef.current.pause();
                          audioRef.current.currentTime = 0;
                        }
                        dispatch(
                          acceptCall({
                            callId: call.id,
                            sourceNumber: callerId || "",
                            actionType: "endAndAccept",
                          }),
                        );
                      }}
                    >
                      <div className="overlappping-btns">
                        <div className="call-btn reject-btn">
                          <HangupIcon />
                        </div>
                        <div className="call-btn accept-btn">
                          <CallAcceptIcon />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <Button
                    type="primary"
                    className="call-accept-btn"
                    shape="circle"
                    size="large"
                    icon={<CallAcceptIcon />}
                    onClick={() => {
                      if (audioRef.current) {
                        audioRef.current.pause();
                        audioRef.current.currentTime = 0;
                      }
                      dispatch(
                        acceptCall({
                          callId: call.id,
                          sourceNumber: callerId || "",
                        }),
                      );
                    }}
                  />
                )}

                <Button
                  type="primary"
                  className="call-hangup-btn"
                  shape="circle"
                  size="large"
                  icon={<HangupIcon />}
                  onClick={() => {
                    if (audioRef.current) {
                      audioRef.current.pause();
                      audioRef.current.currentTime = 0;
                    }
                    dispatch(rejectCall(call.id));
                  }}
                />
              </Flex>
            </Flex>
          </List.Item>
        )}
      />
    </div>
  );
};

export default IncomingCall;
